import { Button, Box, Typography } from '@mui/material';
import {
  mapProjectModuleToExternalDeliveryType,
  mapProjectModuleToInternalDeliveryType,
  mapProjectModuleToInboundLabel,
  mapProjectModuleToOutboundLabel
} from '../../../project-modules/mappings';
import { deliveryTypeToIconMap } from '../../../deliveries/maps/delivery-type-to-icon.map';
import { convertHyphenated } from '../../../../core/util/string-conversion';
import { All } from '../../../../ui/components/pure/icons/All.icon';
import { Colors } from '../../../../ui/theme/colors';

const addButtonStyle = { color: Colors.collectedGreen, borderColor: Colors.collectedGreen };
const removeButtonStyle = { color: Colors.cancelledDeletedRed, borderColor: Colors.cancelledDeletedRed };

type SetPendingFn = (module: string, type: 'internal' | 'external', action: 'add' | 'remove') => void;
type SetAllPendingFn = (action: 'add' | 'remove') => void;

export interface AllocationPanelProps {
  hasSelection: boolean;
  externalModules: string[];
  internalModules: string[];
  onSetPending: SetPendingFn;
  onSetAllPending: SetAllPendingFn;
}

export function AllocationPanel({
  hasSelection,
  externalModules,
  internalModules,
  onSetPending,
  onSetAllPending
}: AllocationPanelProps) {
  return (
    <Box>
      <AllRow hasSelection={hasSelection} onSetAllPending={onSetAllPending} />
      {externalModules.map((name) => (
        <ModuleRow
          key={`external-${name}`}
          name={name}
          type={ModuleRowType.External}
          hasSelection={hasSelection}
          onSetPending={onSetPending}
        />
      ))}
      {internalModules.map((name) => (
        <ModuleRow
          key={`internal-${name}`}
          name={name}
          type={ModuleRowType.Internal}
          hasSelection={hasSelection}
          onSetPending={onSetPending}
        />
      ))}
    </Box>
  );
}

function AllRow({ hasSelection, onSetAllPending }: { hasSelection: boolean; onSetAllPending: SetAllPendingFn }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1
      }}
    >
      <Box
        style={{ gap: 8 }}
        sx={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <All style={{ maxWidth: 26, maxHeight: 26 }} />
        <Typography variant="body1">All Types</Typography>
      </Box>
      <Button
        size="small"
        variant="outlined"
        disabled={!hasSelection}
        style={hasSelection ? addButtonStyle : undefined}
        onClick={() => onSetAllPending('add')}
      >
        Set on selected
      </Button>
    </Box>
  );
}

enum ModuleRowType {
  Internal = 'internal',
  External = 'external'
}

function ModuleRow({
  name,
  type,
  hasSelection,
  onSetPending
}: {
  name: string;
  type: ModuleRowType;
  hasSelection: boolean;
  onSetPending: SetPendingFn;
}) {
  const Icon = getModuleIcon(name, type);
  const label = getModuleLabel(name, type);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1
      }}
    >
      <Box
        style={{ gap: 8, cursor: hasSelection ? 'pointer' : 'default' }}
        onClick={() => hasSelection && onSetPending(name, type, 'add')}
        sx={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {Icon && <Icon style={{ maxWidth: 26, maxHeight: 26 }} />}
        <Typography variant="body1">{label}</Typography>
      </Box>
      <Box
        style={{ gap: 8 }}
        sx={{
          display: 'flex'
        }}
      >
        <Button
          size="small"
          variant="outlined"
          disabled={!hasSelection}
          style={hasSelection ? addButtonStyle : undefined}
          onClick={() => onSetPending(name, type, 'add')}
        >
          Add
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!hasSelection}
          style={hasSelection ? removeButtonStyle : undefined}
          onClick={() => onSetPending(name, type, 'remove')}
        >
          Remove
        </Button>
      </Box>
    </Box>
  );
}

function getModuleLabel(name: string, type: ModuleRowType) {
  if (type === ModuleRowType.External) {
    return mapProjectModuleToInboundLabel(name) || convertHyphenated(name, { capitalizeEachWord: true });
  }
  return mapProjectModuleToOutboundLabel(name) || convertHyphenated(name, { capitalizeEachWord: true });
}

function getModuleIcon(name: string, type: ModuleRowType) {
  const deliveryType =
    type === ModuleRowType.External
      ? mapProjectModuleToExternalDeliveryType(name)
      : mapProjectModuleToInternalDeliveryType(name);
  return deliveryType ? deliveryTypeToIconMap[deliveryType] : undefined;
}
